import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { colors } from './theme';
import LoadingSpinner from './components/LoadingSpinner';
import { SUBTYPE_LABELS } from './utils/complaintCategories';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { handleImagePaste } from './utils/pasteFiles';

const API_BASE = process.env.REACT_APP_API_URL;

const STATUS_LABELS = {
  draft: 'Draft', submitted: 'Submitted', acknowledged: 'Acknowledged',
  initial_report: 'Initial Report', fast_close_pending: 'Fast Close — Awaiting Payment',
  fast_close_confirmed: 'Payment Confirmed', '8d_report': '8D Report',
  corrective_action: 'Corrective Action', monitoring: 'Monitoring',
  closed: 'Closed', archived: 'Archived',
};

const SUPPLIER_STEPS = [
  { key: 'submitted',         label: 'Received' },
  { key: 'acknowledged',      label: 'Acknowledged' },
  { key: 'initial_report',    label: 'Initial Report' },
  { key: '8d_report',         label: '8D Report' },
  { key: 'corrective_action', label: 'Corrective Action' },
  { key: 'monitoring',        label: 'Monitoring' },
  { key: 'closed',            label: 'Closed' },
];

const STATUS_ORDER = ['draft','submitted','acknowledged','initial_report','fast_close_pending','fast_close_confirmed','8d_report','corrective_action','monitoring','closed','archived'];
function stepIndex(status) { return STATUS_ORDER.indexOf(status); }

function getSupplierAction(complaint) {
  const { status, eightDReport, correctiveAction, eightDReview, caReview, infoRequests, fastClose } = complaint;
  const hasOpenInfoRequest = Array.isArray(infoRequests) && infoRequests.some(r => r.status === 'open');

  if (status === 'submitted') return { who: 'you', label: 'Acknowledge this complaint', tab: 'acknowledge' };
  if (status === 'acknowledged' && hasOpenInfoRequest) return { who: 'nin', label: 'North Invent is responding to your information request' };
  if (status === 'acknowledged') return { who: 'you', label: 'Submit your initial investigation report', tab: 'initial_report' };
  if (status === 'initial_report' && complaint.faultChallenge?.status === 'challenged') return { who: 'you', label: 'North Invent is challenging your customer-fault assessment', tab: 'initial_report' };
  if (status === 'initial_report') return { who: 'nin', label: 'North Invent is reviewing your initial report' };
  if (status === 'fast_close_pending' && complaint.fastClose?.gpvDeclinedAt) return { who: 'nin', label: 'North Invent is reviewing your counter-offer' };
  if (status === 'fast_close_pending') return { who: 'you', label: `Confirm payment of €${fastClose?.compensationRequested?.toLocaleString() || '—'} has been sent to North Invent`, tab: 'fast_close' };
  if (status === 'fast_close_confirmed') return { who: 'nin', label: 'North Invent is confirming receipt of payment' };
  if (status === '8d_report' && !eightDReport?.submittedAt) return { who: 'you', label: 'Submit your 8D report', tab: '8d_report' };
  if (status === '8d_report' && eightDReview?.status === 'needs_more') return { who: 'you', label: 'North Invent has requested more detail on your 8D report', tab: '8d_report' };
  if (status === '8d_report' && eightDReport?.submittedAt) return { who: 'nin', label: 'North Invent is reviewing your 8D report' };
  if (status === 'corrective_action' && !correctiveAction?.submittedAt) return { who: 'you', label: 'Submit your corrective action plan', tab: 'corrective_action' };
  if (status === 'corrective_action' && caReview?.status === 'challenged') return { who: 'you', label: 'North Invent proposed a different monitoring schedule', tab: 'corrective_action' };
  if (status === 'corrective_action' && correctiveAction?.submittedAt) return { who: 'nin', label: 'North Invent is reviewing your corrective action plan' };
  if (status === 'monitoring') {
    const mon = complaint.monitoring || {};
    const lots = mon.lots || [];
    const total = mon.totalLots || 0;
    const failurePending = lots.some(l => l.failureAction?.status === 'requested');
    if (failurePending) return { who: 'you', label: 'Corrective action required for a failed lot', tab: 'monitoring' };
    // Once the schedule is complete the supplier has nothing left to do — it is
    // North Invent's move to close. Claiming otherwise left both sides showing
    // ACTION REQUIRED for months and made the flag meaningless.
    if (total && lots.length >= total) {
      return { who: 'nin', label: 'All monitoring lots submitted — North Invent is reviewing and will close the case' };
    }
    if (lots.length && lots.every(l => !l.verifiedAt)) {
      return { who: 'nin', label: 'North Invent is verifying your submitted lots' };
    }
    return { who: 'you', label: `Submit monitoring lot results${total ? ` (${lots.length} of ${total} submitted)` : ''}`, tab: 'monitoring' };
  }
  const comp = complaint.compensation || {};
  if (comp.status === 'accepted' && !comp.paymentSentAt) return { who: 'you', label: `Compensation accepted — please transfer €${(comp.acceptedTotal ?? comp.totalClaimed ?? 0).toLocaleString()} and confirm`, tab: 'compensation' };
  if (status === 'closed' || status === 'archived') return { who: 'done', label: 'This complaint has been closed' };
  return null;
}

export default function SupplierComplaintPage() {
  const { token } = useParams();
  const [complaint, setComplaint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  // Form state
  // Who at the supplier is acting. Every action was previously recorded as the
  // literal string "Supplier", leaving 8D sign-offs and payment declarations
  // unattributable. Remembered per browser so it is typed once.
  const [actorName, setActorName] = useState(() => {
    try { return window.localStorage.getItem('ncr-actor-name') || ''; } catch { return ''; }
  });
  const rememberActor = (value) => {
    setActorName(value);
    try { window.localStorage.setItem('ncr-actor-name', value); } catch { /* private mode */ }
  };
  const [ackNote, setAckNote] = useState('');
  const [irContent, setIrContent] = useState('');
  const [irFaultType, setIrFaultType] = useState('workmanship');
  const [irFiles, setIrFiles] = useState([]);
  const [infoReqDesc, setInfoReqDesc] = useState('');
  const [infoReqTasks, setInfoReqTasks] = useState('');
  const [infoReqFiles, setInfoReqFiles] = useState([]);
  const [d8Summary, setD8Summary] = useState('');
  const [d8Files, setD8Files] = useState([]);
  const [d8Fields, setD8Fields] = useState({ d1_team:'',d2_problem:'',d3_containment:'',d4_rootCause:'',d5_correctiveActions:'',d6_implementation:'',d7_prevention:'',d8_closure:'' });
  const [d8RespondAction, setD8RespondAction] = useState('resubmit');
  const [d8RespondNote, setD8RespondNote] = useState('');
  const [d8RespondFiles, setD8RespondFiles] = useState([]);
  const [caDesc, setCaDesc] = useState('');
  const [, setCaDate] = useState('');
  const [caLots, setCaLots] = useState('');
  const [caTotalLots, setCaTotalLots] = useState('');
  const [caFiles, setCaFiles] = useState([]);
  const [caRespondLots, setCaRespondLots] = useState('');
  const [caRespondTotal, setCaRespondTotal] = useState('');
  const [caRespondNote, setCaRespondNote] = useState('');
  const [fcNote, setFcNote] = useState('');
  const [fcDeclineNote, setFcDeclineNote] = useState('');
  const [fcCounterAmount, setFcCounterAmount] = useState('');
  const [faultChallengeAccepts, setFaultChallengeAccepts] = useState(false);
  const [faultChallengeNote, setFaultChallengeNote] = useState('');
  const [lotResult, setLotResult] = useState('pass');
  const [lotNotes, setLotNotes] = useState('');
  const [lotFiles, setLotFiles] = useState([]);
  const [compAction, setCompAction] = useState('accept');
  const [compCounterHours, setCompCounterHours] = useState('');
  const [compCounterBom, setCompCounterBom] = useState('');
  const [compNote, setCompNote] = useState('');
  const [compPaymentNote, setCompPaymentNote] = useState('');
  const [failureRespondLot, setFailureRespondLot] = useState(null);
  const [failureRespondNote, setFailureRespondNote] = useState('');
  const [failureRespondFiles, setFailureRespondFiles] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/complaints/public/${token}`);
        const data = await res.json();
        if (!data.ok) { setError('This link is invalid or has expired.'); return; }
        const c = data.complaint;
        setComplaint(c);
        if (c.initialReport?.content) setIrContent(c.initialReport.content);
        const ft = c.initialReport?.faultType;
        if (ft && ft !== 'tbd') setIrFaultType(ft);
        if (c.correctiveAction?.description) setCaDesc(c.correctiveAction.description);
        if (c.correctiveAction?.targetDate) setCaDate(c.correctiveAction.targetDate.split('T')[0]);
        if (c.correctiveAction?.proposedLotsPerMonth) setCaLots(c.correctiveAction.proposedLotsPerMonth);
        if (c.correctiveAction?.proposedTotalLots) setCaTotalLots(c.correctiveAction.proposedTotalLots);
        if (c.caReview?.challengeLotsPerMonth) setCaRespondLots(c.caReview.challengeLotsPerMonth);
        if (c.caReview?.challengeTotalLots) setCaRespondTotal(c.caReview.challengeTotalLots);
        const r = c.eightDReport || {};
        setD8Summary(r.summary || '');
        setD8Fields({ d1_team:r.d1_team||'',d2_problem:r.d2_problem||'',d3_containment:r.d3_containment||'',d4_rootCause:r.d4_rootCause||'',d5_correctiveActions:r.d5_correctiveActions||'',d6_implementation:r.d6_implementation||'',d7_prevention:r.d7_prevention||'',d8_closure:r.d8_closure||'' });
        const action = getSupplierAction(c);
        if (action?.tab) setActiveTab(action.tab);
      } catch (e) { setError('Failed to load. Please try again.'); }
      finally { setLoading(false); }
    };
    load();
  }, [token]);

  const post = async (path, body, isFormData = false) => {
    setSubmitting(true); setActionError(''); setActionSuccess('');
    try {
      const opts = { method: 'POST' };
      if (isFormData) { opts.body = body; }
      else { opts.headers = { 'Content-Type': 'application/json' }; opts.body = JSON.stringify(body); }
      const res = await fetch(`${API_BASE}${path}`, opts);
      const data = await res.json();
      if (!data.ok) { setActionError(data.message || data.error || 'Submission failed.'); return null; }
      return data;
    } catch (e) { setActionError('Network error. Please try again.'); return null; }
    finally { setSubmitting(false); }
  };

  const handleAcknowledge = async () => {
    const d = await post(`/complaints/public/${token}/acknowledge`, { note: ackNote });
    if (d) { setComplaint(d.complaint); setActionSuccess('Complaint acknowledged. You can now prepare your initial report.'); setActiveTab('initial_report'); }
  };

  const handleInitialReport = async () => {
    if (!irContent.trim()) { setActionError('Please provide report content.'); return; }
    const fd = new FormData();
    fd.append('by', actorName);
    fd.append('content', irContent);
    fd.append('faultType', irFaultType);
    irFiles.forEach(f => fd.append('files', f));
    const d = await post(`/complaints/public/${token}/initial-report`, fd, true);
    if (d) { setComplaint(d.complaint); setActionSuccess('Initial report submitted. North Invent will review it.'); }
  };

  const handleInfoRequest = async () => {
    if (!infoReqDesc.trim()) { setActionError('Please describe what information you need.'); return; }
    const fd = new FormData();
    fd.append('by', actorName);
    fd.append('description', infoReqDesc);
    const tasks = infoReqTasks.split('\n').map(t => t.trim()).filter(Boolean);
    if (tasks.length) fd.append('tasks', JSON.stringify(tasks));
    infoReqFiles.forEach(f => fd.append('files', f));
    const d = await post(`/complaints/public/${token}/info-request`, fd, true);
    if (d) { setComplaint(d.complaint); setInfoReqDesc(''); setInfoReqTasks(''); setInfoReqFiles([]); setActionSuccess('Information request sent to North Invent.'); }
  };

  const handle8D = async () => {
    const fd = new FormData();
    fd.append('by', actorName);
    fd.append('summary', d8Summary);
    Object.entries(d8Fields).forEach(([k,v]) => fd.append(k, v));
    d8Files.forEach(f => fd.append('files', f));
    const d = await post(`/complaints/public/${token}/8d-report`, fd, true);
    if (d) { setComplaint(d.complaint); setActionSuccess('8D report submitted. North Invent will review it.'); }
  };

  const handle8DRespond = async () => {
    const fd = new FormData();
    fd.append('by', actorName);
    fd.append('action', d8RespondAction);
    fd.append('note', d8RespondNote);
    fd.append('summary', d8Summary);
    d8RespondFiles.forEach(f => fd.append('files', f));
    const d = await post(`/complaints/public/${token}/8d-respond`, fd, true);
    if (d) { setComplaint(d.complaint); setActionSuccess(d8RespondAction === 'decline' ? 'Response sent.' : '8D report updated and resubmitted.'); }
  };

  const handleCA = async () => {
    if (!caDesc.trim()) { setActionError('Please provide a description.'); return; }
    const fd = new FormData();
    fd.append('by', actorName);
    fd.append('description', caDesc);
    // Auto-calculate target date from lots
    if (caLots && caTotalLots) {
      const months = Math.ceil(Number(caTotalLots) / Number(caLots));
      const targetDate = new Date();
      targetDate.setMonth(targetDate.getMonth() + months);
      fd.append('targetDate', targetDate.toISOString());
    }
    fd.append('proposedLotsPerMonth', caLots);
    fd.append('proposedTotalLots', caTotalLots);
    caFiles.forEach(f => fd.append('files', f));
    const d = await post(`/complaints/public/${token}/corrective-action`, fd, true);
    if (d) { setComplaint(d.complaint); setActionSuccess('Corrective action plan submitted.'); }
  };

  const handleCARespond = async () => {
    const d = await post(`/complaints/public/${token}/ca-respond`, {
      by: actorName,
      lotsPerMonth: caRespondLots,
      totalLots: caRespondTotal,
      note: caRespondNote,
    });
    if (d) { setComplaint(d.complaint); setActionSuccess('Response sent to North Invent.'); }
  };

  const handleFastCloseConfirm = async () => {
    const d = await post(`/complaints/public/${token}/fast-close-confirm`, { note: fcNote });
    if (d) { setComplaint(d.complaint); setActionSuccess('Payment confirmed. North Invent will close the case once they verify receipt.'); }
  };

  const handleFastCloseDecline = async () => {
    const d = await post(`/complaints/public/${token}/fast-close-decline`, {
      by: actorName,
      note: fcDeclineNote,
      counterAmount: fcCounterAmount || undefined,
    });
    if (d) { setComplaint(d.complaint); setFcDeclineNote(''); setFcCounterAmount(''); setActionSuccess('Response sent to North Invent.'); }
  };

  const handleFaultChallengeRespond = async () => {
    const d = await post(`/complaints/public/${token}/fault-challenge-respond`, {
      by: actorName,
      acceptsResponsibility: faultChallengeAccepts,
      note: faultChallengeNote,
    });
    if (d) { setComplaint(d.complaint); setFaultChallengeNote(''); setActionSuccess('Response sent to North Invent.'); }
  };

  const handleLotSubmit = async () => {
    const fd = new FormData();
    fd.append('by', actorName);
    const now = new Date();
    fd.append('result', lotResult);
    fd.append('notes', lotNotes);
    fd.append('month', now.getMonth() + 1);
    fd.append('year', now.getFullYear());
    lotFiles.forEach(f => fd.append('files', f));
    const d = await post(`/complaints/public/${token}/monitoring-lot`, fd, true);
    if (d) { setComplaint(d.complaint); setLotNotes(''); setLotFiles([]); setActionSuccess('Lot result submitted.'); }
  };

  const handleCompRespond = async () => {
    const d = await post(`/complaints/public/${token}/compensation-respond`, {
      by: actorName,
      action: compAction,
      counterHours: compCounterHours || undefined,
      counterBom: compCounterBom || undefined,
      note: compNote,
    });
    if (d) { setComplaint(d.complaint); setCompNote(''); setActionSuccess(compAction === 'accept' ? 'Compensation accepted.' : 'Counter-offer sent to North Invent.'); }
  };

  const handleCompPaymentSent = async () => {
    const d = await post(`/complaints/public/${token}/compensation-payment-sent`, { note: compPaymentNote });
    if (d) { setComplaint(d.complaint); setCompPaymentNote(''); setActionSuccess('Payment confirmation sent to North Invent.'); }
  };

  const handleLotFailureRespond = async (lotNumber) => {
    const fd = new FormData();
    fd.append('by', actorName);
    fd.append('lotNumber', lotNumber);
    fd.append('note', failureRespondNote);
    failureRespondFiles.forEach(f => fd.append('files', f));
    const d = await post(`/complaints/public/${token}/monitoring-lot-failure-respond`, fd, true);
    if (d) { setComplaint(d.complaint); setFailureRespondLot(null); setFailureRespondNote(''); setFailureRespondFiles([]); setActionSuccess('Corrective action response sent.'); }
  };

  if (loading) return (
    <div style={{ minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background: colors.page }}>
      <LoadingSpinner size={56} />
    </div>
  );
  if (error || !complaint) return (
    <div style={{ minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',padding:'2rem',background: colors.page }}>
      <div style={{ textAlign:'center', maxWidth: 400 }}>
        <p style={{ color: colors.danger, fontSize:'1.1rem', fontWeight: 600 }}>{error||'Not found.'}</p>
        <p style={{ color: colors.textMuted, fontSize:'0.88rem' }}>Check your link or contact the North Invent team.</p>
      </div>
    </div>
  );

  const { status, complaintId, title } = complaint;
  const action = getSupplierAction(complaint);
  const currentStepIdx = stepIndex(status);
  const isAckDone = !!complaint.acknowledgement?.at;
  const isIRDone = !!complaint.initialReport?.submittedAt;
  const is8DDone = !!complaint.eightDReport?.submittedAt;
  const isCADone = !!complaint.correctiveAction?.submittedAt;
  const isFastClose = ['fast_close_pending','fast_close_confirmed'].includes(status);
  const fastCloseNeedsGPVAction = status === 'fast_close_pending' && !complaint.fastClose?.gpvDeclinedAt;
  const needsMoreInfo = complaint.eightDReview?.status === 'needs_more';
  const caChallenged = complaint.caReview?.status === 'challenged';

  const comp = complaint.compensation || {};
  const compNeedsAction = comp.status === 'accepted' && !comp.paymentSentAt;
  const failureLotsPendingResponse = (complaint.monitoring?.lots || []).filter(l => l.failureAction?.status === 'requested');
  const TABS = [
    { key: 'overview', label: 'Overview' },
    ...(!isAckDone && status === 'submitted' ? [{ key: 'acknowledge', label: '⚡ Acknowledge' }] : []),
    ...(isAckDone && !isFastClose ? [{ key: 'initial_report', label: isIRDone ? 'Initial Report ✓' : (status === 'acknowledged' ? '⚡ Initial Report' : 'Initial Report') }] : []),
    ...(isFastClose ? [{ key: 'fast_close', label: fastCloseNeedsGPVAction ? '⚡ Confirm Payment' : status === 'fast_close_confirmed' ? 'Fast Close ✓' : 'Fast Close' }] : []),
    ...(['8d_report','corrective_action','monitoring','closed','archived'].includes(status) || is8DDone
      ? [{ key: '8d_report', label: is8DDone && !needsMoreInfo ? '8D Report ✓' : (status === '8d_report' && (!is8DDone || needsMoreInfo) ? '⚡ 8D Report' : '8D Report') }] : []),
    ...(['corrective_action','monitoring','closed','archived'].includes(status) || isCADone
      ? [{ key: 'corrective_action', label: isCADone && !caChallenged ? 'Corrective Action ✓' : (status === 'corrective_action' && (!isCADone || caChallenged) ? '⚡ Corrective Action' : 'Corrective Action') }] : []),
    ...(complaint.monitoring?.startedAt ? [{ key: 'monitoring', label: failureLotsPendingResponse.length > 0 ? '⚡ Monitoring' : status === 'monitoring' ? 'Monitoring' : 'Monitoring' }] : []),
    ...(['8d_report','corrective_action','monitoring','closed','archived'].includes(status)
      ? [{ key: 'compensation', label: compNeedsAction ? '⚡ Compensation' : comp.status === 'payment_received' ? 'Compensation ✓' : 'Compensation' }] : []),
  ];

  return (
    <div style={{ minHeight:'100vh', background: colors.page, color: colors.textBody }}>
      <div style={{ maxWidth: 760, margin:'0 auto', padding:'2rem 1rem' }}>
        <div style={{ marginBottom:'1.5rem' }}>
          <img src="/north-invent-logo.svg" alt="North Invent" style={{ height:28, marginBottom:'1.25rem', display:'block' }} />
          <div style={{ fontSize:'0.7rem', color:colors.textDim, textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:700 }}>Non-Conformance Report</div>
          <h1 style={{ margin:'0.25rem 0 0.2rem', fontSize:'1.4rem', color:colors.textStrong }}>{complaintId}</h1>
          <p style={{ margin:0, color:colors.textBody, fontSize:'0.95rem' }}>{title}</p>
          <div style={{ marginTop:'0.5rem', display:'flex', gap:'0.5rem', alignItems:'center', flexWrap:'wrap' }}>
            <span style={{ fontSize:'0.75rem', fontWeight:700, color:colors.primary }}>{complaint.supplier?.toUpperCase()}</span>
            <span style={{ fontSize:'0.72rem', color:colors.textMuted, background:colors.surfaceRaised, padding:'0.1rem 0.45rem', borderRadius:4 }}>{STATUS_LABELS[status]||status}</span>
          </div>
        </div>

        {/* Progress stepper */}
        <div style={{ display:'flex', alignItems:'center', gap:0, marginBottom:'1.5rem', overflowX:'auto', paddingBottom:'0.25rem' }}>
          {SUPPLIER_STEPS.map((step, i) => {
            const stepIdx = stepIndex(step.key);
            const done = stepIdx < currentStepIdx;
            const active = stepIdx === currentStepIdx;
            return (
              <React.Fragment key={step.key}>
                {i > 0 && <div style={{ flex:'1 1 8px', height:2, minWidth:8, background: done ? colors.primary : colors.panelBorder }} />}
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', flexShrink:0 }}>
                  <div style={{ width:28, height:28, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.7rem', fontWeight:700, background: done ? colors.primary : active ? colors.surface : colors.surfaceMuted, border: `2px solid ${done ? colors.primary : active ? colors.primary : colors.panelBorder}`, color: done ? '#000' : active ? colors.primary : colors.textDim }}>
                    {done ? '✓' : i + 1}
                  </div>
                  <div style={{ fontSize:'0.6rem', color: active ? colors.primary : done ? colors.textMuted : colors.textDim, fontWeight: active ? 700 : 400, marginTop:'0.2rem', textAlign:'center', maxWidth:60, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{step.label}</div>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Who is acting. Everything a supplier submits — an 8D sign-off, a
            payment declaration — is recorded against this name, so it is asked
            for once and remembered. */}
        {action?.who === 'you' && (
          <div style={{ marginBottom:'1rem', display:'flex', alignItems:'center', gap:'0.6rem', flexWrap:'wrap' }}>
            <label style={{ fontSize:'0.72rem', color:colors.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em' }}>
              Your name
            </label>
            <input
              value={actorName}
              onChange={e => rememberActor(e.target.value)}
              placeholder="Name and company, e.g. Anna Berg (GPV)"
              className="ni-input"
              style={{ flex:1, minWidth:220, marginBottom:0 }}
            />
            {!actorName.trim() && (
              <span style={{ fontSize:'0.72rem', color:'#fbbf24' }}>
                Needed so your responses are attributable
              </span>
            )}
          </div>
        )}

        {/* Action banner */}
        {action && (
          <div style={{ padding:'1rem 1.25rem', borderRadius:10, marginBottom:'1.25rem', background: action.who === 'you' ? `${colors.danger}18` : action.who === 'done' ? `${colors.success}15` : `${colors.primary}12`, border: `1px solid ${action.who === 'you' ? colors.danger+'40' : action.who === 'done' ? colors.success+'30' : colors.primary+'30'}`, display:'flex', alignItems:'flex-start', gap:'0.75rem' }}>
            <span style={{ fontSize:'1.2rem', flexShrink:0 }}>{action.who === 'you' ? '⚡' : action.who === 'done' ? '✅' : '⏳'}</span>
            <div>
              {action.who === 'you' && <div style={{ fontWeight:700, fontSize:'0.82rem', color:colors.danger, marginBottom:'0.15rem' }}>ACTION REQUIRED</div>}
              {action.who === 'nin' && <div style={{ fontWeight:700, fontSize:'0.82rem', color:colors.primary, marginBottom:'0.15rem' }}>WAITING FOR NORTH INVENT</div>}
              {action.who === 'done' && <div style={{ fontWeight:700, fontSize:'0.82rem', color:colors.success, marginBottom:'0.15rem' }}>COMPLETED</div>}
              <div style={{ fontSize:'0.82rem', color:colors.textBody }}>{action.label}</div>
              {action.tab && (
                <button type="button" onClick={() => setActiveTab(action.tab)} style={{ marginTop:'0.5rem', background:'none', border:'none', cursor:'pointer', fontSize:'0.78rem', color:colors.danger, fontWeight:700, padding:0, textDecoration:'underline' }}>
                  Go to action →
                </button>
              )}
            </div>
          </div>
        )}

        {actionError && <div style={{ background:`${colors.danger}18`, border:`1px solid ${colors.danger}40`, borderRadius:8, padding:'0.65rem 0.85rem', color:colors.danger, fontSize:'0.85rem', marginBottom:'1rem' }}>{actionError}</div>}
        {actionSuccess && <div style={{ background:`${colors.success}15`, border:`1px solid ${colors.success}30`, borderRadius:8, padding:'0.65rem 0.85rem', color:colors.success, fontSize:'0.85rem', marginBottom:'1rem' }}>{actionSuccess}</div>}

        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:`1px solid ${colors.panelBorder}`, marginBottom:'1.5rem', overflowX:'auto' }}>
          {TABS.map(t => (
            <button key={t.key} type="button" onClick={() => setActiveTab(t.key)} style={{ background:'none', border:'none', cursor:'pointer', padding:'0.6rem 0.9rem', fontSize:'0.78rem', fontWeight:600, color: activeTab === t.key ? colors.primary : colors.textMuted, borderBottom: activeTab === t.key ? `2px solid ${colors.primary}` : '2px solid transparent', whiteSpace:'nowrap' }}>
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && <OverviewSection complaint={complaint} />}
        {activeTab === 'acknowledge' && <AcknowledgeSection complaint={complaint} ackNote={ackNote} setAckNote={setAckNote} onSubmit={handleAcknowledge} submitting={submitting} />}
        {activeTab === 'initial_report' && (
          <InitialReportSection
            complaint={complaint}
            content={irContent} setContent={setIrContent}
            faultType={irFaultType} setFaultType={setIrFaultType}
            files={irFiles} setFiles={setIrFiles}
            onSubmit={handleInitialReport}
            infoReqDesc={infoReqDesc} setInfoReqDesc={setInfoReqDesc}
            infoReqTasks={infoReqTasks} setInfoReqTasks={setInfoReqTasks}
            infoReqFiles={infoReqFiles} setInfoReqFiles={setInfoReqFiles}
            onInfoRequest={handleInfoRequest}
            faultChallengeAccepts={faultChallengeAccepts} setFaultChallengeAccepts={setFaultChallengeAccepts}
            faultChallengeNote={faultChallengeNote} setFaultChallengeNote={setFaultChallengeNote}
            onFaultChallengeRespond={handleFaultChallengeRespond}
            submitting={submitting}
          />
        )}
        {activeTab === 'fast_close' && <FastCloseSection complaint={complaint} fcNote={fcNote} setFcNote={setFcNote} onConfirm={handleFastCloseConfirm} fcDeclineNote={fcDeclineNote} setFcDeclineNote={setFcDeclineNote} fcCounterAmount={fcCounterAmount} setFcCounterAmount={setFcCounterAmount} onDecline={handleFastCloseDecline} submitting={submitting} />}
        {activeTab === '8d_report' && (
          <EightDSection
            complaint={complaint}
            summary={d8Summary} setSummary={setD8Summary}
            fields={d8Fields} setFields={setD8Fields}
            files={d8Files} setFiles={setD8Files}
            onSubmit={handle8D}
            respondAction={d8RespondAction} setRespondAction={setD8RespondAction}
            respondNote={d8RespondNote} setRespondNote={setD8RespondNote}
            respondFiles={d8RespondFiles} setRespondFiles={setD8RespondFiles}
            onRespond={handle8DRespond}
            submitting={submitting}
          />
        )}
        {activeTab === 'corrective_action' && (
          <CorrectiveActionSection
            complaint={complaint}
            desc={caDesc} setDesc={setCaDesc}
            lots={caLots} setLots={setCaLots}
            totalLots={caTotalLots} setTotalLots={setCaTotalLots}
            files={caFiles} setFiles={setCaFiles}
            onSubmit={handleCA}
            respondLots={caRespondLots} setRespondLots={setCaRespondLots}
            respondTotal={caRespondTotal} setRespondTotal={setCaRespondTotal}
            respondNote={caRespondNote} setRespondNote={setCaRespondNote}
            onRespond={handleCARespond}
            submitting={submitting}
          />
        )}
        {activeTab === 'monitoring' && (
          <MonitoringSection
            complaint={complaint}
            lotResult={lotResult} setLotResult={setLotResult}
            lotNotes={lotNotes} setLotNotes={setLotNotes}
            lotFiles={lotFiles} setLotFiles={setLotFiles}
            onSubmitLot={handleLotSubmit}
            failureRespondLot={failureRespondLot} setFailureRespondLot={setFailureRespondLot}
            failureRespondNote={failureRespondNote} setFailureRespondNote={setFailureRespondNote}
            failureRespondFiles={failureRespondFiles} setFailureRespondFiles={setFailureRespondFiles}
            onFailureRespond={handleLotFailureRespond}
            submitting={submitting}
          />
        )}
        {activeTab === 'compensation' && (
          <CompensationSection
            complaint={complaint}
            compAction={compAction} setCompAction={setCompAction}
            compCounterHours={compCounterHours} setCompCounterHours={setCompCounterHours}
            compCounterBom={compCounterBom} setCompCounterBom={setCompCounterBom}
            compNote={compNote} setCompNote={setCompNote}
            compPaymentNote={compPaymentNote} setCompPaymentNote={setCompPaymentNote}
            onRespond={handleCompRespond}
            onPaymentSent={handleCompPaymentSent}
            submitting={submitting}
          />
        )}

        <div style={{ marginTop:'3rem', paddingTop:'1.25rem', borderTop:`1px solid ${colors.panelBorder}`, fontSize:'0.72rem', color:colors.textDim }}>
          This complaint was filed by North Invent AS. For questions, contact your North Invent quality representative.
        </div>
      </div>
    </div>
  );
}

// ── Sections ──────────────────────────────────────────────────────────────────

const snapshotMdComponents = {
  h1: ({ children }) => <h1 style={{ fontSize: 18, fontWeight: 800, borderBottom: `1px solid ${colors.panelBorder}`, paddingBottom: 8, marginTop: 0, color: colors.textStrong }}>{children}</h1>,
  h2: ({ children }) => <h2 style={{ fontSize: 14, fontWeight: 700, marginTop: 20, marginBottom: 6, color: colors.textStrong }}>{children}</h2>,
  h3: ({ children }) => <h3 style={{ fontSize: 13, fontWeight: 700, marginTop: 14, color: colors.textBody }}>{children}</h3>,
  p: ({ children }) => <p style={{ fontSize: 13, lineHeight: 1.7, color: colors.textBody, margin: '0 0 10px' }}>{children}</p>,
  blockquote: ({ children }) => (
    <blockquote style={{ borderLeft: `3px solid ${colors.primary}`, margin: '10px 0', padding: '8px 14px', background: `${colors.primary}08`, borderRadius: '0 5px 5px 0' }}>
      {children}
    </blockquote>
  ),
  table: ({ children }) => <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 12 }}>{children}</table>,
  th: ({ children }) => <th style={{ padding: '6px 10px', background: 'rgba(0,0,0,0.2)', textAlign: 'left', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', border: `1px solid ${colors.panelBorder}` }}>{children}</th>,
  td: ({ children }) => <td style={{ padding: '6px 10px', border: `1px solid ${colors.panelBorder}`, fontSize: 12, color: colors.textBody }}>{children}</td>,
  ul: ({ children }) => <ul style={{ paddingLeft: 18, marginBottom: 10 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ paddingLeft: 18, marginBottom: 10 }}>{children}</ol>,
  li: ({ children }) => <li style={{ marginBottom: 3, fontSize: 13, color: colors.textBody }}>{children}</li>,
  hr: () => <hr style={{ border: 'none', borderTop: `1px solid ${colors.panelBorder}`, margin: '14px 0' }} />,
  strong: ({ children }) => <strong style={{ fontWeight: 700, color: colors.textStrong }}>{children}</strong>,
  code: ({ children }) => <code style={{ background: 'rgba(0,0,0,0.25)', borderRadius: 4, padding: '2px 5px', fontSize: 11, fontFamily: 'monospace', color: colors.primary }}>{children}</code>,
};

function QualityLinksCard({ links }) {
  const [expanded, setExpanded] = useState(null);
  if (!links || links.length === 0) return null;
  return (
    <Card title="Referenced Quality Procedures">
      <p style={{ color: colors.textMuted, fontSize: '0.8rem', margin: '0 0 0.75rem' }}>
        North Invent has identified the following quality procedure(s) as relevant to this non-conformance. Please review them carefully before responding.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {links.map((l, i) => (
          <div key={l._id || i} style={{ border: `1px solid ${colors.primary}40`, borderRadius: 8, overflow: 'hidden', background: `${colors.primary}08` }}>
            <div style={{ padding: '0.75rem 1rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: colors.primary, fontWeight: 800 }}>{l.documentNumber}</span>
                    <span style={{ fontSize: '0.72rem', background: `${colors.primary}25`, color: colors.primary, padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>Rev. {l.revision}</span>
                    {l.sectionRef && (
                      <span style={{ fontSize: '0.72rem', color: colors.textMuted, background: colors.surfaceRaised, padding: '2px 8px', borderRadius: 4 }}>{l.sectionRef}</span>
                    )}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: colors.textStrong }}>{l.documentTitle}</div>
                  {l.note && (
                    <div style={{ marginTop: 6, padding: '6px 10px', background: `${colors.danger}12`, border: `1px solid ${colors.danger}30`, borderRadius: 5 }}>
                      <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.danger }}>Non-conformance noted: </span>
                      <span style={{ fontSize: '0.82rem', color: colors.textBody }}>{l.note}</span>
                    </div>
                  )}
                </div>
                {l.snapshotContent && (
                  <button
                    onClick={() => setExpanded(expanded === i ? null : i)}
                    style={{ background: 'none', border: `1px solid ${colors.panelBorder}`, color: colors.textMuted, borderRadius: 5, padding: '4px 12px', fontSize: '0.75rem', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}
                  >
                    {expanded === i ? 'Hide procedure' : 'View procedure'}
                  </button>
                )}
              </div>
            </div>
            {expanded === i && l.snapshotContent && (
              <div style={{ borderTop: `1px solid ${colors.panelBorder}`, padding: '1.25rem 1.5rem', background: colors.surface, maxHeight: 520, overflowY: 'auto' }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.textDim, marginBottom: 14 }}>
                  {l.documentNumber} — Rev. {l.revision} · Frozen at time of complaint
                </div>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={snapshotMdComponents}>
                  {l.snapshotContent}
                </ReactMarkdown>
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

function OverviewSection({ complaint }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem' }}>
      <QualityLinksCard links={complaint.qualityLinks} />
      {complaint.description && (
        <Card title="Description">
          <p style={{ color:colors.textBody, fontSize:'0.9rem', margin:0, whiteSpace:'pre-wrap' }}>{complaint.description}</p>
        </Card>
      )}
      {/* Only worth a card when at least one file is actually retrievable —
          legacy disk-stored evidence has no url and would render as dead rows. */}
      {complaint.evidence?.some(f => f.url) && (
        <Card title="Evidence from North Invent">
          <AttachedFiles files={complaint.evidence.filter(f => f.url)} label="" />
        </Card>
      )}
      <Card title="Details">
        <div style={{ display:'flex', flexWrap:'wrap', gap:'1.25rem' }}>
          {complaint.productNumber && <Pair label="Product No." value={complaint.productNumber} />}
          {complaint.batchNumber && <Pair label="Batch No." value={complaint.batchNumber} />}
          {complaint.serialNumber && <Pair label="Serial No." value={complaint.serialNumber} />}
          {complaint.quantity != null && <Pair label="Quantity" value={complaint.quantity} />}
          {complaint.submittedAt && <Pair label="Filed on" value={new Date(complaint.submittedAt).toLocaleDateString('en-CA')} />}
        </div>
      </Card>
      {complaint.complaintTypes?.length > 0 && (
        <Card title="Complaint Types">
          <div style={{ display:'flex', flexWrap:'wrap', gap:'0.4rem' }}>
            {complaint.complaintTypes.map(t => (
              <span key={t} style={{ fontSize:'0.78rem', background:colors.surfaceRaised, color:colors.textMuted, padding:'0.2rem 0.6rem', borderRadius:4 }}>
                {SUBTYPE_LABELS[t] || t.replace(/_/g,' ')}
              </span>
            ))}
          </div>
        </Card>
      )}
      <Card title="Compensation Claimed by North Invent">
        <div style={{ display:'flex', flexWrap:'wrap', gap:'1.25rem' }}>
          <Pair label="Hours" value={complaint.compensation?.hoursSpent||0} />
          <Pair label="Rate" value={`€${complaint.compensation?.hourlyRate||0}/h`} />
          <Pair label="BOM" value={`€${complaint.compensation?.bomCost||0}`} />
          <Pair label="Total claimed" value={<strong style={{ color:colors.statusYellow }}>€{(complaint.compensation?.totalClaimed||0).toLocaleString()}</strong>} />
        </div>
      </Card>
    </div>
  );
}

function AcknowledgeSection({ complaint, ackNote, setAckNote, onSubmit, submitting }) {
  const done = !!complaint.acknowledgement?.at;
  if (done) return (
    <Card>
      <p style={{ color:colors.success, fontWeight:600 }}>✓ Acknowledged on {new Date(complaint.acknowledgement.at).toLocaleDateString('en-CA')}</p>
      {complaint.acknowledgement.note && <p style={{ color:colors.textMuted, fontSize:'0.88rem', margin:'0.5rem 0 0' }}>{complaint.acknowledgement.note}</p>}
    </Card>
  );
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
      <p style={{ color:colors.textBody, fontSize:'0.9rem', margin:0 }}>
        By acknowledging, you confirm you have received and understood this non-conformance report. You are committing to investigate and respond.
      </p>
      <div>
        <FieldLabel>Acknowledgement Note (optional)</FieldLabel>
        <textarea value={ackNote} onChange={e => setAckNote(e.target.value)} className="ni-input" rows={4} placeholder="Add a note or initial response..." style={{ resize:'vertical', marginTop:'0.4rem' }} />
      </div>
      <button onClick={onSubmit} disabled={submitting} className="ni-button ni-button--primary" style={{ alignSelf:'flex-start' }}>
        {submitting ? 'Acknowledging…' : 'Acknowledge Complaint'}
      </button>
    </div>
  );
}

function InitialReportSection({ complaint, content, setContent, faultType, setFaultType, files, setFiles, onSubmit, infoReqDesc, setInfoReqDesc, infoReqTasks, setInfoReqTasks, infoReqFiles, setInfoReqFiles, onInfoRequest, faultChallengeAccepts, setFaultChallengeAccepts, faultChallengeNote, setFaultChallengeNote, onFaultChallengeRespond, submitting }) {
  const done = !!complaint.initialReport?.submittedAt;
  const canSubmit = complaint.status === 'acknowledged';
  const [showInfoReq, setShowInfoReq] = useState(false);
  const openRequests = complaint.infoRequests?.filter(r => r.status === 'open') || [];
  const resolvedRequests = complaint.infoRequests?.filter(r => r.status === 'resolved') || [];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
      {done && (
        <div style={{ background:`${colors.success}15`, border:`1px solid ${colors.success}30`, borderRadius:8, padding:'0.65rem 0.85rem', color:colors.success, fontSize:'0.82rem', fontWeight:600 }}>
          ✓ Submitted on {new Date(complaint.initialReport.submittedAt).toLocaleDateString('en-CA')} by {complaint.initialReport.submittedBy}
          {complaint.initialReport.faultType && complaint.initialReport.faultType !== 'tbd' && (
            <span style={{ marginLeft: '0.75rem', color: complaint.initialReport.faultType === 'workmanship' ? colors.danger : colors.statusYellow }}>
              · {complaint.initialReport.faultType === 'workmanship' ? 'Workmanship' : 'Customer Fault'}
            </span>
          )}
        </div>
      )}

      {/* Resolved info requests */}
      {resolvedRequests.map(req => (
        <div key={req.id} style={{ background:`${colors.success}08`, border:`1px solid ${colors.success}20`, borderRadius:8, padding:'0.75rem' }}>
          <FieldLabel>Information Request — Resolved</FieldLabel>
          <p style={{ color:colors.textMuted, fontSize:'0.85rem', margin:'0.3rem 0 0' }}>{req.resolvedNote || 'Resolved'}</p>
          {req.resolvedFiles?.length > 0 && (
            <div style={{ marginTop:'0.4rem' }}>
              <AttachedFiles files={req.resolvedFiles} label="Files from North Invent" />
            </div>
          )}
        </div>
      ))}

      {/* Open info requests waiting for NIN */}
      {openRequests.map(req => (
        <div key={req.id} style={{ background:`${colors.statusYellow}10`, border:`1px solid ${colors.statusYellow}40`, borderRadius:8, padding:'0.75rem' }}>
          <FieldLabel style={{ color: colors.statusYellow }}>Awaiting Response from North Invent</FieldLabel>
          <p style={{ color:colors.textBody, fontSize:'0.88rem', margin:'0.4rem 0' }}>{req.description}</p>
          {req.tasks?.length > 0 && (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.25rem' }}>
              {req.tasks.map(t => <div key={t.id} style={{ fontSize:'0.82rem', color: t.done ? colors.success : colors.textMuted }}>
                {t.done ? '✓' : '○'} {t.text}
              </div>)}
            </div>
          )}
        </div>
      ))}

      <p style={{ color:colors.textMuted, fontSize:'0.82rem', margin:0 }}>
        Describe your immediate findings and containment actions. What happened, what was affected, and what have you done to stop it spreading?
      </p>

      <div>
        <FieldLabel>Fault Assessment *</FieldLabel>
        <p style={{ color:colors.textDim, fontSize:'0.75rem', margin:'0.2rem 0 0.4rem' }}>Select whether this is a workmanship defect on your part, or a customer-caused issue.</p>
        <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.4rem', flexWrap:'wrap' }}>
          {[['workmanship','Workmanship — Our fault'],['customer_fault','Customer fault']].map(([v,l]) => (
            <button key={v} type="button" disabled={done} onClick={() => setFaultType(v)} style={{ padding:'0.45rem 0.85rem', borderRadius:6, fontSize:'0.82rem', fontWeight:600, cursor: done ? 'default' : 'pointer', background: faultType === v ? (v === 'workmanship' ? colors.danger : colors.statusYellow) : colors.surfaceRaised, color: faultType === v ? '#000' : colors.textMuted, border: `1px solid ${faultType === v ? 'transparent' : colors.panelBorder}` }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div>
        <FieldLabel>Report Content *</FieldLabel>
        <textarea value={content} onChange={e => setContent(e.target.value)} className="ni-input" rows={8} placeholder="Initial investigation findings and immediate containment actions taken..." style={{ resize:'vertical', marginTop:'0.4rem' }} readOnly={done && !canSubmit} />
      </div>

      <FileUploadField files={files} setFiles={setFiles} label="Attachments (photos, test reports, etc.)" disabled={done && !canSubmit} />
      <AttachedFiles files={complaint.initialReport?.files} />

      {/* Fault challenge response — shown if NIN challenged the assessment */}
      {complaint.faultChallenge?.status === 'challenged' && (
        <div style={{ background:`${colors.danger}10`, border:`1px solid ${colors.danger}30`, borderRadius:8, padding:'0.85rem' }}>
          <FieldLabel style={{ color: colors.danger }}>North Invent Disputes Your Fault Assessment</FieldLabel>
          <p style={{ color:colors.textBody, fontSize:'0.88rem', margin:'0.4rem 0 0.75rem', whiteSpace:'pre-wrap' }}>
            {complaint.faultChallenge.challengeNote || 'North Invent believes this may be a workmanship defect and is asking you to reconsider.'}
          </p>
          <FieldLabel>Your response *</FieldLabel>
          <div style={{ display:'flex', gap:'0.5rem', margin:'0.4rem 0 0.65rem', flexWrap:'wrap' }}>
            <button type="button" onClick={() => setFaultChallengeAccepts(true)} className={`ni-button ${faultChallengeAccepts ? 'ni-button--primary' : 'ni-button--ghost'}`} style={{ fontSize:'0.82rem', background: faultChallengeAccepts ? colors.danger : undefined, borderColor: faultChallengeAccepts ? colors.danger : undefined }}>
              We accept responsibility — Workmanship defect
            </button>
            <button type="button" onClick={() => setFaultChallengeAccepts(false)} className={`ni-button ${!faultChallengeAccepts ? 'ni-button--primary' : 'ni-button--ghost'}`} style={{ fontSize:'0.82rem' }}>
              We maintain: this is a customer fault
            </button>
          </div>
          <FieldLabel>Explanation (required)</FieldLabel>
          <textarea value={faultChallengeNote} onChange={e => setFaultChallengeNote(e.target.value)} className="ni-input" rows={3} placeholder="Provide evidence or explanation for your position..." style={{ resize:'vertical', marginTop:'0.3rem', marginBottom:'0.5rem' }} />
          <button onClick={onFaultChallengeRespond} disabled={submitting || !faultChallengeNote.trim()} className="ni-button ni-button--primary">
            {submitting ? 'Sending…' : 'Submit Response'}
          </button>
        </div>
      )}

      {canSubmit && !done && (
        <>
          <button onClick={onSubmit} disabled={submitting} className="ni-button ni-button--primary" style={{ alignSelf:'flex-start' }}>
            {submitting ? 'Submitting…' : 'Submit Initial Report'}
          </button>

          {!showInfoReq && (
            <button type="button" onClick={() => setShowInfoReq(true)} className="ni-button ni-button--ghost" style={{ alignSelf:'flex-start', fontSize:'0.8rem' }}>
              Need more information from North Invent first?
            </button>
          )}

          {showInfoReq && (
            <div style={{ background:`${colors.surfaceMuted}`, borderRadius:8, padding:'0.85rem', border:`1px solid ${colors.panelBorder}` }}>
              <FieldLabel>Request more information from North Invent</FieldLabel>
              <p style={{ color:colors.textMuted, fontSize:'0.78rem', margin:'0.3rem 0 0.65rem' }}>Describe what you need. You can also add tasks for North Invent to complete before you can submit your report.</p>
              <textarea value={infoReqDesc} onChange={e => setInfoReqDesc(e.target.value)} className="ni-input" rows={3} placeholder="What information do you need from North Invent?" style={{ resize:'vertical', marginBottom:'0.5rem' }} />
              <div>
                <FieldLabel>Tasks (one per line, optional)</FieldLabel>
                <textarea value={infoReqTasks} onChange={e => setInfoReqTasks(e.target.value)} className="ni-input" rows={3} placeholder="Provide serial number batch list&#10;Send photos of affected units&#10;Confirm production date range" style={{ resize:'vertical', marginTop:'0.3rem', marginBottom:'0.5rem' }} />
              </div>
              <FileUploadField files={infoReqFiles} setFiles={setInfoReqFiles} label="Attachments" />
              <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.5rem' }}>
                <button onClick={onInfoRequest} disabled={submitting} className="ni-button ni-button--primary" style={{ fontSize:'0.82rem' }}>{submitting ? 'Sending…' : 'Send Request'}</button>
                <button onClick={() => setShowInfoReq(false)} className="ni-button ni-button--ghost" style={{ fontSize:'0.82rem' }}>Cancel</button>
              </div>
            </div>
          )}
        </>
      )}
      {!canSubmit && !done && (
        <p style={{ color:colors.textDim, fontSize:'0.82rem' }}>Available once the complaint has been acknowledged.</p>
      )}
    </div>
  );
}

function FastCloseSection({ complaint, fcNote, setFcNote, onConfirm, fcDeclineNote, setFcDeclineNote, fcCounterAmount, setFcCounterAmount, onDecline, submitting }) {
  const fc = complaint.fastClose || {};
  const done = !!fc.paymentConfirmedAt;
  const [showDecline, setShowDecline] = useState(false);

  if (done) return (
    <Card>
      <p style={{ color:colors.success, fontWeight:600 }}>✓ Payment confirmed on {new Date(fc.paymentConfirmedAt).toLocaleDateString('en-CA')}</p>
      {fc.paymentNote && <p style={{ color:colors.textMuted, fontSize:'0.88rem', margin:'0.5rem 0 0' }}>{fc.paymentNote}</p>}
    </Card>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
      <Card>
        <p style={{ color:colors.textBody, fontSize:'0.9rem', margin:0 }}>North Invent has accepted the initial report and is requesting direct compensation instead of proceeding to 8D.</p>
        {fc.note && <p style={{ color:colors.textMuted, fontSize:'0.85rem', margin:'0.65rem 0 0', fontStyle:'italic' }}>"{fc.note}"</p>}
      </Card>

      {/* Negotiation history */}
      {fc.negotiations?.length > 0 && (
        <div style={{ background:colors.surfaceMuted, borderRadius:8, padding:'0.75rem' }}>
          <FieldLabel>Negotiation History</FieldLabel>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.35rem', marginTop:'0.4rem' }}>
            {fc.negotiations.map((n, i) => (
              <div key={i} style={{ display:'flex', gap:'0.65rem', fontSize:'0.82rem', color:colors.textMuted }}>
                <span style={{ fontWeight:700, color: n.from === 'nin' ? colors.primary : colors.statusYellow, minWidth:32 }}>{n.from.toUpperCase()}</span>
                {n.amount != null && <span>€{n.amount.toLocaleString()}</span>}
                {n.note && <span style={{ color:colors.textDim }}>— {n.note}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ background:`${colors.statusYellow}12`, borderRadius:8, padding:'0.85rem', border:`1px solid ${colors.statusYellow}35` }}>
        <FieldLabel>Amount requested</FieldLabel>
        <div style={{ fontSize:'1.5rem', fontWeight:800, color:colors.statusYellow, marginTop:'0.3rem' }}>
          €{fc.compensationRequested?.toLocaleString() || '—'}
        </div>
      </div>

      {!showDecline ? (
        <>
          <p style={{ color:colors.textMuted, fontSize:'0.85rem', margin:0 }}>
            Once you have transferred the payment, confirm below. North Invent will verify receipt and close the case.
          </p>
          <div>
            <FieldLabel>Payment reference / note (optional)</FieldLabel>
            <input value={fcNote} onChange={e => setFcNote(e.target.value)} className="ni-input" placeholder="Bank transfer reference or note..." style={{ marginTop:'0.4rem' }} />
          </div>
          <div style={{ display:'flex', gap:'0.65rem', flexWrap:'wrap' }}>
            <button onClick={onConfirm} disabled={submitting} className="ni-button ni-button--primary">
              {submitting ? 'Confirming…' : 'Confirm Payment Has Been Sent'}
            </button>
            <button type="button" onClick={() => setShowDecline(true)} className="ni-button ni-button--ghost" style={{ fontSize:'0.82rem' }}>
              Decline or counter-propose
            </button>
          </div>
        </>
      ) : (
        <div style={{ background:`${colors.danger}10`, border:`1px solid ${colors.danger}30`, borderRadius:8, padding:'0.85rem' }}>
          <FieldLabel style={{ color: colors.danger }}>Decline or Counter-offer</FieldLabel>
          <p style={{ color:colors.textMuted, fontSize:'0.78rem', margin:'0.3rem 0 0.65rem' }}>
            Explain why you cannot accept this amount and optionally propose a different amount. North Invent will review your response.
          </p>
          <div style={{ display:'flex', gap:'0.65rem', flexWrap:'wrap', marginBottom:'0.5rem' }}>
            <div style={{ flex:'1 1 140px' }}>
              <FieldLabel>Counter-offer (€, optional)</FieldLabel>
              <input type="number" value={fcCounterAmount} onChange={e => setFcCounterAmount(e.target.value)} className="ni-input" placeholder="Leave blank to decline outright" style={{ marginTop:'0.3rem' }} />
            </div>
            <div style={{ flex:'2 1 200px' }}>
              <FieldLabel>Explanation *</FieldLabel>
              <input value={fcDeclineNote} onChange={e => setFcDeclineNote(e.target.value)} className="ni-input" placeholder="Reason for declining or counter-proposing..." style={{ marginTop:'0.3rem' }} />
            </div>
          </div>
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <button onClick={onDecline} disabled={submitting || !fcDeclineNote.trim()} className="ni-button ni-button--primary" style={{ fontSize:'0.82rem' }}>
              {submitting ? 'Sending…' : fcCounterAmount ? 'Send Counter-offer' : 'Decline Fast-Close'}
            </button>
            <button type="button" onClick={() => setShowDecline(false)} className="ni-button ni-button--ghost" style={{ fontSize:'0.82rem' }}>Back</button>
          </div>
        </div>
      )}
    </div>
  );
}

function EightDSection({ complaint, summary, setSummary, fields, setFields, files, setFiles, onSubmit, respondAction, setRespondAction, respondNote, setRespondNote, respondFiles, setRespondFiles, onRespond, submitting }) {
  const done = !!complaint.eightDReport?.submittedAt;
  const canSubmit = complaint.status === '8d_report' && !done;
  const needsMore = complaint.eightDReview?.status === 'needs_more';
  const canRespond = done && needsMore;
  const setF = (k,v) => setFields(prev => ({ ...prev, [k]:v }));
  const [showDetail, setShowDetail] = useState(false);

  const D_FIELDS = [
    ['d1_team','D1 — Team','List all team members responsible for resolving this issue.'],
    ['d2_problem','D2 — Problem Statement','Describe the problem specifically and measurably.'],
    ['d3_containment','D3 — Containment Actions','What immediate actions stopped further impact on the customer?'],
    ['d4_rootCause','D4 — Root Cause Analysis','What is the verified root cause? Include contributing factors.'],
    ['d5_correctiveActions','D5 — Corrective Actions','Which actions permanently eliminate the root cause?'],
    ['d6_implementation','D6 — Implementation','Who implements which actions, by when?'],
    ['d7_prevention','D7 — Systemic Prevention','How will you prevent recurrence across similar products/processes?'],
    ['d8_closure','D8 — Team & Customer Sign-off','Confirmation that all actions are complete and team/customer satisfied.'],
  ];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
      {done && (
        <div style={{ background:`${colors.success}15`, border:`1px solid ${colors.success}30`, borderRadius:8, padding:'0.65rem 0.85rem', color:colors.success, fontSize:'0.82rem', fontWeight:600 }}>
          ✓ Submitted on {new Date(complaint.eightDReport.submittedAt).toLocaleDateString('en-CA')} by {complaint.eightDReport.submittedBy}
        </div>
      )}
      {/* Decline response sent — supplier declined needs-more, NIN notified */}
      {done && complaint.eightDReview?.supplierDeclined && complaint.eightDReview?.status === 'pending' && !needsMore && !complaint.eightDReport?.resubmittedAt && (
        <div style={{ background:`${colors.statusYellow}12`, border:`1px solid ${colors.statusYellow}35`, borderRadius:8, padding:'0.85rem' }}>
          <div style={{ fontWeight:700, fontSize:'0.88rem', color:colors.statusYellow, marginBottom:'0.3rem' }}>Response sent — decline recorded</div>
          <p style={{ color:colors.textMuted, fontSize:'0.82rem', margin:0 }}>
            You indicated the 8D report is complete as submitted. North Invent will review your position.
          </p>
        </div>
      )}

      {/* Needs-more request from NIN */}
      {needsMore && (
        <div style={{ background:`${colors.statusYellow}15`, border:`1px solid ${colors.statusYellow}40`, borderRadius:8, padding:'0.85rem' }}>
          <FieldLabel style={{ color: colors.statusYellow }}>North Invent requests more detail</FieldLabel>
          <p style={{ color:colors.textBody, fontSize:'0.88rem', margin:'0.4rem 0' }}>{complaint.eightDReview.needsMoreNote}</p>
          <p style={{ color:colors.textMuted, fontSize:'0.78rem', margin:0 }}>You can provide additional information and resubmit, or decline if you believe the report is complete.</p>
        </div>
      )}

      {!canSubmit && !done && complaint.status !== '8d_report' && (
        <div style={{ padding:'1.5rem', textAlign:'center' }}>
          <p style={{ color:colors.textDim, fontSize:'0.85rem' }}>The 8D report will be requested by North Invent after reviewing your initial report.</p>
        </div>
      )}

      {(canSubmit || done) && (
        <>
          <p style={{ color:colors.textMuted, fontSize:'0.82rem', margin:0 }}>
            The 8D report is the core analysis document. Attaching your own 8D as a PDF is enough —
            the fields below are an optional alternative if you would rather fill in our template.
          </p>
          <div>
            <FieldLabel>Summary / Notes (optional)</FieldLabel>
            <textarea value={summary} onChange={e => setSummary(e.target.value)} className="ni-input" rows={4} placeholder="Summary of findings, or leave blank if uploading a PDF..." style={{ resize:'vertical', marginTop:'0.4rem' }} readOnly={done && !canRespond} />
          </div>
          <FileUploadField files={files} setFiles={setFiles} label="8D Report / PDF / Supporting Documents" disabled={done && !canRespond} />
          {done && !canRespond && (
            // The upload control is hidden once the 8D is in — say so, rather
            // than leaving a blank space that reads as "PDFs not accepted".
            <p style={{ margin: 0, fontSize: '0.78rem', color: colors.textMuted }}>
              Your 8D report has been submitted, so attachments are locked. If you need to add a
              document, North Invent can reopen it by requesting more detail.
            </p>
          )}
          <AttachedFiles files={complaint.eightDReport?.files} />

          <div>
            <button type="button" onClick={() => setShowDetail(!showDetail)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:'0.78rem', color:colors.textMuted, padding:0, textDecoration:'underline' }}>
              {showDetail ? 'Hide detailed 8D fields' : 'Fill in detailed 8D fields (optional)'}
            </button>
          </div>

          {showDetail && D_FIELDS.map(([key, label, hint]) => (
            <div key={key}>
              <FieldLabel>{label}</FieldLabel>
              <p style={{ fontSize:'0.72rem', color:colors.textDim, margin:'0.2rem 0 0.35rem' }}>{hint}</p>
              <textarea value={fields[key]} onChange={e => setF(key, e.target.value)} className="ni-input" rows={2} style={{ resize:'vertical' }} readOnly={done && !canRespond} />
            </div>
          ))}

          {canSubmit && !canRespond && (
            <button onClick={onSubmit} disabled={submitting} className="ni-button ni-button--primary" style={{ alignSelf:'flex-start' }}>
              {submitting ? 'Submitting…' : 'Submit 8D Report'}
            </button>
          )}
        </>
      )}

      {/* After resubmit — show confirmation when resubmittedAt is set and NIN review is back to pending */}
      {done && complaint.eightDReport?.resubmittedAt && complaint.eightDReview?.status === 'pending' && !needsMore && (
        <div style={{ background:`${colors.success}15`, border:`1px solid ${colors.success}35`, borderRadius:8, padding:'0.85rem' }}>
          <div style={{ fontWeight:700, fontSize:'0.88rem', color:colors.success, marginBottom:'0.3rem' }}>✓ Additional information sent</div>
          <p style={{ color:colors.textMuted, fontSize:'0.82rem', margin:0 }}>
            Your updated 8D report was resubmitted on {new Date(complaint.eightDReport.resubmittedAt).toLocaleDateString('en-CA')}. North Invent will review it and get back to you.
          </p>
        </div>
      )}

      {/* Respond to needs-more */}
      {canRespond && (
        <div style={{ borderTop:`1px solid ${colors.panelBorder}`, paddingTop:'1rem' }}>
          <FieldLabel>Your response</FieldLabel>
          <div style={{ display:'flex', gap:'0.5rem', margin:'0.5rem 0', flexWrap:'wrap' }}>
            <button type="button" onClick={() => setRespondAction('resubmit')} className={`ni-button ${respondAction === 'resubmit' ? 'ni-button--primary' : 'ni-button--ghost'}`} style={{ fontSize:'0.82rem' }}>Provide more & resubmit</button>
            <button type="button" onClick={() => setRespondAction('decline')} className={`ni-button ${respondAction === 'decline' ? 'ni-button--primary' : 'ni-button--ghost'}`} style={{ fontSize:'0.82rem' }}>Decline — this report is complete</button>
          </div>
          {respondAction === 'decline' && (
            <textarea value={respondNote} onChange={e => setRespondNote(e.target.value)} className="ni-input" rows={2} placeholder="Explain why no further detail will be provided..." style={{ resize:'vertical', marginBottom:'0.5rem' }} />
          )}
          {respondAction === 'resubmit' && (
            <FileUploadField files={respondFiles} setFiles={setRespondFiles} label="Additional files" />
          )}
          <button onClick={onRespond} disabled={submitting} className="ni-button ni-button--primary" style={{ marginTop:'0.5rem' }}>
            {submitting ? 'Sending…' : respondAction === 'decline' ? 'Send Decline Response' : 'Resubmit with Additional Info'}
          </button>
        </div>
      )}
    </div>
  );
}

function CorrectiveActionSection({ complaint, desc, setDesc, lots, setLots, totalLots, setTotalLots, files, setFiles, onSubmit, respondLots, setRespondLots, respondTotal, setRespondTotal, respondNote, setRespondNote, onRespond, submitting }) {
  const done = !!complaint.correctiveAction?.submittedAt;
  const canSubmit = complaint.status === 'corrective_action' && !done;
  const challenged = complaint.caReview?.status === 'challenged';
  const estimatedMonths = lots && totalLots ? Math.ceil(Number(totalLots) / Number(lots)) : null;
  const challengeMonths = respondLots && respondTotal ? Math.ceil(Number(respondTotal) / Number(respondLots)) : null;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
      {done && (
        <div style={{ background:`${colors.success}15`, border:`1px solid ${colors.success}30`, borderRadius:8, padding:'0.65rem 0.85rem', color:colors.success, fontSize:'0.82rem', fontWeight:600 }}>
          ✓ Submitted on {new Date(complaint.correctiveAction.submittedAt).toLocaleDateString('en-CA')} by {complaint.correctiveAction.submittedBy}
          {complaint.correctiveAction.targetDate && (
            <span style={{ color:colors.textMuted, fontWeight:400, marginLeft:'0.75rem' }}>
              · Target: {new Date(complaint.correctiveAction.targetDate).toLocaleDateString('en-CA')}
            </span>
          )}
        </div>
      )}

      {/* CA Challenge from NIN */}
      {challenged && (
        <div style={{ background:`${colors.statusYellow}15`, border:`1px solid ${colors.statusYellow}40`, borderRadius:8, padding:'0.85rem' }}>
          <FieldLabel style={{ color: colors.statusYellow }}>North Invent proposed a different monitoring schedule</FieldLabel>
          <div style={{ display:'flex', gap:'1.25rem', flexWrap:'wrap', marginTop:'0.4rem' }}>
            <div><div style={{ fontSize:'0.65rem', color:colors.textDim, fontWeight:700, textTransform:'uppercase' }}>Lots / month</div><div style={{ fontSize:'1rem', fontWeight:700, color:colors.textBody }}>{complaint.caReview.challengeLotsPerMonth}</div></div>
            <div><div style={{ fontSize:'0.65rem', color:colors.textDim, fontWeight:700, textTransform:'uppercase' }}>Total lots</div><div style={{ fontSize:'1rem', fontWeight:700, color:colors.textBody }}>{complaint.caReview.challengeTotalLots}</div></div>
            {complaint.caReview.challengeLotsPerMonth && complaint.caReview.challengeTotalLots && (
              <div><div style={{ fontSize:'0.65rem', color:colors.textDim, fontWeight:700, textTransform:'uppercase' }}>Duration</div><div style={{ fontSize:'1rem', fontWeight:700, color:colors.textBody }}>~{Math.ceil(complaint.caReview.challengeTotalLots / complaint.caReview.challengeLotsPerMonth)} months</div></div>
            )}
          </div>
          {complaint.caReview.challengeNote && <p style={{ color:colors.textMuted, fontSize:'0.82rem', margin:'0.5rem 0 0' }}>{complaint.caReview.challengeNote}</p>}

          <div style={{ marginTop:'1rem' }}>
            <FieldLabel>Your response</FieldLabel>
            <div style={{ display:'flex', gap:'0.65rem', flexWrap:'wrap', margin:'0.5rem 0' }}>
              <div style={{ flex:'1 1 120px' }}>
                <FieldLabel>Lots / month</FieldLabel>
                <input type="number" value={respondLots} onChange={e => setRespondLots(e.target.value)} className="ni-input" style={{ marginTop:'0.3rem' }} min={1} />
              </div>
              <div style={{ flex:'1 1 120px' }}>
                <FieldLabel>Total lots</FieldLabel>
                <input type="number" value={respondTotal} onChange={e => setRespondTotal(e.target.value)} className="ni-input" style={{ marginTop:'0.3rem' }} min={1} />
              </div>
              {challengeMonths && <div style={{ alignSelf:'flex-end', paddingBottom:'0.35rem', fontSize:'0.82rem', color:colors.textMuted }}>~{challengeMonths} months</div>}
            </div>
            <FieldLabel>Note (optional)</FieldLabel>
            <input value={respondNote} onChange={e => setRespondNote(e.target.value)} className="ni-input" placeholder="Reason for your proposal..." style={{ marginTop:'0.3rem', marginBottom:'0.5rem' }} />
            <button onClick={onRespond} disabled={submitting} className="ni-button ni-button--primary">{submitting ? 'Sending…' : 'Send Response'}</button>
          </div>
        </div>
      )}

      {!canSubmit && !done && complaint.status !== 'corrective_action' && (
        <p style={{ color:colors.textDim, fontSize:'0.85rem' }}>Available after North Invent accepts your 8D report.</p>
      )}

      {(canSubmit || done) && !challenged && (
        <>
          <p style={{ color:colors.textMuted, fontSize:'0.82rem', margin:0 }}>
            Describe specifically what will change in your process to prevent recurrence. Include your proposed monitoring schedule.
          </p>
          <div>
            <FieldLabel>Corrective Action Plan *</FieldLabel>
            <textarea value={desc} onChange={e => setDesc(e.target.value)} className="ni-input" rows={7} placeholder="Describe all corrective actions in detail..." style={{ resize:'vertical', marginTop:'0.4rem' }} readOnly={done} />
          </div>
          <div style={{ display:'flex', gap:'0.65rem', flexWrap:'wrap', alignItems:'flex-end' }}>
            <div style={{ flex:'1 1 120px' }}>
              <FieldLabel>Proposed Lots / Month</FieldLabel>
              <input type="number" value={lots} onChange={e => setLots(e.target.value)} className="ni-input" style={{ marginTop:'0.35rem' }} min={1} readOnly={done} />
            </div>
            <div style={{ flex:'1 1 120px' }}>
              <FieldLabel>Proposed Total Lots</FieldLabel>
              <input type="number" value={totalLots} onChange={e => setTotalLots(e.target.value)} className="ni-input" style={{ marginTop:'0.35rem' }} min={1} readOnly={done} />
            </div>
            {estimatedMonths && (
              <div style={{ paddingBottom:'0.4rem', display:'flex', flexDirection:'column', gap:'0.1rem' }}>
                <FieldLabel>Est. completion</FieldLabel>
                <div style={{ fontSize:'0.88rem', color:colors.primary, fontWeight:600, marginTop:'0.35rem' }}>
                  {(() => { const d = new Date(); d.setMonth(d.getMonth() + estimatedMonths); return d.toLocaleDateString('en-CA'); })()}
                  <span style={{ color:colors.textDim, fontWeight:400, fontSize:'0.78rem', marginLeft:'0.4rem' }}>~{estimatedMonths} months</span>
                </div>
              </div>
            )}
          </div>
          <FileUploadField files={files} setFiles={setFiles} label="Attachments (updated procedures, test plans, etc.)" disabled={done} />
          <AttachedFiles files={complaint.correctiveAction?.files} />
          {canSubmit && (
            <button onClick={onSubmit} disabled={submitting} className="ni-button ni-button--primary" style={{ alignSelf:'flex-start' }}>
              {submitting ? 'Submitting…' : 'Submit Corrective Action Plan'}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function MonitoringSection({ complaint, lotResult, setLotResult, lotNotes, setLotNotes, lotFiles, setLotFiles, onSubmitLot,
  failureRespondLot, setFailureRespondLot, failureRespondNote, setFailureRespondNote, failureRespondFiles, setFailureRespondFiles, onFailureRespond, submitting }) {
  const mon = complaint.monitoring || {};
  const lots = mon.lots || [];
  const done = lots.filter(l => l.result !== 'pending').length;
  const passed = lots.filter(l => l.result === 'pass').length;
  const failed = lots.filter(l => l.result === 'fail').length;
  const nextLotNumber = lots.length + 1;
  const allDone = mon.totalLots && lots.length >= mon.totalLots;
  const estimatedMonths = mon.lotsPerMonth && mon.totalLots ? Math.ceil(mon.totalLots / mon.lotsPerMonth) : null;
  const failureLotsPending = lots.filter(l => l.result === 'fail' && l.failureAction?.status === 'requested');

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
      {failureLotsPending.length > 0 && (
        <Card>
          <div style={{ background:`${colors.danger}10`, border:`1px solid ${colors.danger}30`, borderRadius:8, padding:'0.75rem 1rem', marginBottom:'0.75rem' }}>
            <div style={{ fontWeight:700, fontSize:'0.85rem', color:colors.danger, marginBottom:'0.25rem' }}>⚡ Action Required — Failed Lot Corrective Action</div>
            <div style={{ fontSize:'0.8rem', color:colors.textMuted }}>North Invent has requested corrective actions for {failureLotsPending.length} failed lot{failureLotsPending.length > 1 ? 's' : ''}. Respond below.</div>
          </div>
          {failureLotsPending.map(lot => (
            <div key={lot.lotNumber} style={{ background:colors.surface, borderRadius:8, border:`1px solid ${colors.danger}40`, padding:'0.75rem', marginBottom:'0.5rem' }}>
              <div style={{ fontWeight:700, fontSize:'0.88rem', color:colors.danger, marginBottom:'0.35rem' }}>Lot #{lot.lotNumber} — FAILED</div>
              {lot.failureAction?.requestNote && <p style={{ color:colors.textMuted, fontSize:'0.85rem', margin:'0 0 0.65rem', whiteSpace:'pre-wrap' }}>{lot.failureAction.requestNote}</p>}
              {failureRespondLot === lot.lotNumber ? (
                <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                  <FieldLabel>Your corrective actions</FieldLabel>
                  <textarea value={failureRespondNote} onChange={e => setFailureRespondNote(e.target.value)} className="ni-input" rows={3}
                    placeholder="Describe the corrective actions you have taken or will take..." style={{ resize:'vertical' }} />
                  <FileUploadField files={failureRespondFiles} setFiles={setFailureRespondFiles} label="Supporting documents (optional)" />
                  <div style={{ display:'flex', gap:'0.5rem' }}>
                    <button onClick={() => onFailureRespond(lot.lotNumber)} disabled={submitting} className="ni-button ni-button--primary" style={{ fontSize:'0.85rem' }}>{submitting ? 'Sending…' : 'Submit Response'}</button>
                    <button onClick={() => setFailureRespondLot(null)} className="ni-button ni-button--ghost" style={{ fontSize:'0.85rem' }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setFailureRespondLot(lot.lotNumber)} className="ni-button ni-button--primary" style={{ fontSize:'0.85rem' }}>
                  Respond with Corrective Actions
                </button>
              )}
            </div>
          ))}
        </Card>
      )}

      <Card title="Monitoring Phase">
        <div style={{ display:'flex', flexWrap:'wrap', gap:'1.25rem', marginBottom:'1rem' }}>
          <Pair label="Lots/Month" value={mon.lotsPerMonth} />
          <Pair label="Total Lots" value={mon.totalLots} />
          {estimatedMonths && <Pair label="Duration" value={`~${estimatedMonths} months`} />}
          <Pair label="Started" value={mon.startedAt ? new Date(mon.startedAt).toLocaleDateString('en-CA') : '—'} />
          <Pair label="Passed" value={<span style={{ color:colors.success, fontWeight:700 }}>{passed}</span>} />
          <Pair label="Failed" value={<span style={{ color:failed>0?colors.danger:colors.textMuted, fontWeight:700 }}>{failed}</span>} />
        </div>
        <div style={{ background:colors.surfaceMuted, borderRadius:6, height:8, overflow:'hidden', marginBottom:'1rem' }}>
          <div style={{ height:'100%', width:`${mon.totalLots ? (done/mon.totalLots)*100 : 0}%`, background: failed>0 ? colors.statusYellow : colors.primary, transition:'width 0.3s' }} />
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:'0.35rem' }}>
          {lots.map(lot => {
            const fa = lot.failureAction || {};
            return (
              <div key={lot.lotNumber} style={{ background:colors.surface, borderRadius:6, border:`1px solid ${lot.result==='fail' && fa.status !== 'accepted' ? colors.danger+'50' : colors.panelBorder}`, fontSize:'0.82rem', padding:'0.5rem 0.65rem' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', flexWrap:'wrap' }}>
                  <span style={{ color:colors.textDim, fontWeight:600 }}>#{lot.lotNumber}</span>
                  <span style={{ fontWeight:700, color: lot.result==='pass'?colors.success:lot.result==='fail'?colors.danger:colors.textDim }}>{lot.result.toUpperCase()}</span>
                  {lot.verifiedAt && <span style={{ fontSize:'0.63rem', background:`${colors.success}20`, color:colors.success, borderRadius:4, padding:'0.1rem 0.4rem', fontWeight:700 }}>✓ VERIFIED</span>}
                  {fa.status === 'requested' && <span style={{ fontSize:'0.63rem', background:`${colors.danger}20`, color:colors.danger, borderRadius:4, padding:'0.1rem 0.4rem', fontWeight:700 }}>ACTION NEEDED</span>}
                  {fa.status === 'responded' && <span style={{ fontSize:'0.63rem', background:`${colors.statusYellow}20`, color:colors.statusYellow, borderRadius:4, padding:'0.1rem 0.4rem', fontWeight:700 }}>RESPONSE SENT</span>}
                  {fa.status === 'accepted' && <span style={{ fontSize:'0.63rem', background:`${colors.success}20`, color:colors.success, borderRadius:4, padding:'0.1rem 0.4rem', fontWeight:700 }}>RESOLVED</span>}
                  {lot.submittedAt && <span style={{ color:colors.textDim, fontSize:'0.72rem' }}>{new Date(lot.submittedAt).toLocaleDateString('en-CA')}</span>}
                  {lot.notes && <span style={{ color:colors.textMuted, flex:1, minWidth:80 }}>{lot.notes}</span>}
                </div>
                {lot.files?.length > 0 && (
                  <div style={{ marginTop:'0.35rem', paddingTop:'0.3rem', borderTop:`1px solid ${colors.panelBorder}` }}>
                    <AttachedFiles files={lot.files} label="" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {complaint.status === 'monitoring' && !allDone && (
        <Card title={`Submit Lot #${nextLotNumber}`}>
          <p style={{ color:colors.textMuted, fontSize:'0.82rem', margin:'0 0 0.75rem' }}>Submit the result for your next production lot. Include evidence of quality checks performed.</p>
          <div style={{ display:'flex', gap:'0.5rem', marginBottom:'0.65rem', flexWrap:'wrap', alignItems:'flex-end' }}>
            <div>
              <FieldLabel>Result</FieldLabel>
              <select value={lotResult} onChange={e => setLotResult(e.target.value)} className="ni-input" style={{ marginTop:'0.3rem', width:'auto' }}>
                <option value="pass">Pass</option>
                <option value="fail">Fail</option>
              </select>
            </div>
            <div style={{ flex:1, minWidth:140 }}>
              <FieldLabel>Notes / observations</FieldLabel>
              <input value={lotNotes} onChange={e => setLotNotes(e.target.value)} className="ni-input" placeholder="Inspection findings..." style={{ marginTop:'0.3rem' }} />
            </div>
          </div>
          <FileUploadField files={lotFiles} setFiles={setLotFiles} label="Evidence (photos, test records)" />
          <button onClick={onSubmitLot} disabled={submitting} className="ni-button ni-button--primary" style={{ marginTop:'0.75rem' }}>
            {submitting ? 'Saving…' : `Submit Lot #${nextLotNumber}`}
          </button>
        </Card>
      )}

      {allDone && (
        <Card>
          <p style={{ color:colors.success, fontWeight:600 }}>✓ All {mon.totalLots} lots submitted. North Invent will review and close the complaint.</p>
        </Card>
      )}
    </div>
  );
}

function CompensationSection({ complaint, compAction, setCompAction, compCounterHours, setCompCounterHours, compCounterBom, setCompCounterBom, compNote, setCompNote, compPaymentNote, setCompPaymentNote, onRespond, onPaymentSent, submitting }) {
  const comp = complaint.compensation || {};
  const alreadyResponded = !!comp.supplierAcceptedAt || comp.status === 'challenged';
  const paymentSent = !!comp.paymentSentAt;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
      <Card title="Compensation Claimed by North Invent">
        <div style={{ display:'flex', flexWrap:'wrap', gap:'1.25rem', marginBottom:'0.75rem' }}>
          <Pair label="Hours" value={comp.hoursSpent||0} />
          <Pair label="Rate" value={`€${comp.hourlyRate||0}/h`} />
          <Pair label="BOM" value={`€${comp.bomCost||0}`} />
          <Pair label="Total claimed" value={<strong style={{ color:colors.statusYellow, fontSize:'1.1rem' }}>€{(comp.totalClaimed||0).toLocaleString()}</strong>} />
        </div>
        {comp.status === 'payment_received' && (
          <div style={{ background:`${colors.success}15`, border:`1px solid ${colors.success}30`, borderRadius:8, padding:'0.65rem', color:colors.success, fontWeight:600, fontSize:'0.85rem' }}>
            ✓ Compensation settled — payment received by North Invent
          </div>
        )}
      </Card>

      {/* Current status */}
      {(comp.status === 'challenged' || comp.status === 'accepted' || comp.status === 'payment_sent' || comp.status === 'payment_received') && (
        <Card title="Status">
          {comp.status === 'challenged' && <p style={{ color:colors.statusYellow, fontWeight:600, fontSize:'0.88rem', margin:0 }}>You have sent a counter-offer. Waiting for North Invent to respond.</p>}
          {comp.status === 'accepted' && !paymentSent && <p style={{ color:colors.primary, fontWeight:600, fontSize:'0.88rem', margin:0 }}>Compensation accepted. Please transfer €{(comp.acceptedTotal ?? comp.totalClaimed ?? 0).toLocaleString()} and confirm below.</p>}
          {paymentSent && comp.status === 'payment_sent' && <p style={{ color:colors.statusYellow, fontWeight:600, fontSize:'0.88rem', margin:0 }}>Payment confirmation sent — waiting for North Invent to verify receipt.</p>}
          {comp.challengeNotes && <p style={{ color:colors.textMuted, fontSize:'0.82rem', margin:'0.5rem 0 0', whiteSpace:'pre-wrap' }}>{comp.challengeNotes}</p>}
        </Card>
      )}

      {/* Accept or challenge — only if not yet responded */}
      {!alreadyResponded && !paymentSent && comp.status !== 'payment_received' && (
        <Card title="Your Response">
          <p style={{ color:colors.textMuted, fontSize:'0.85rem', margin:'0 0 0.85rem' }}>
            Review the claim above and either accept the full amount or propose a counter-offer.
          </p>
          <div style={{ display:'flex', gap:'1rem', marginBottom:'0.85rem' }}>
            <label style={{ display:'flex', alignItems:'center', gap:'0.4rem', cursor:'pointer', fontSize:'0.88rem', color: compAction==='accept' ? colors.success : colors.textMuted, fontWeight: compAction==='accept' ? 700 : 400 }}>
              <input type="radio" value="accept" checked={compAction==='accept'} onChange={() => setCompAction('accept')} /> Accept full claim
            </label>
            <label style={{ display:'flex', alignItems:'center', gap:'0.4rem', cursor:'pointer', fontSize:'0.88rem', color: compAction==='challenge' ? colors.statusYellow : colors.textMuted, fontWeight: compAction==='challenge' ? 700 : 400 }}>
              <input type="radio" value="challenge" checked={compAction==='challenge'} onChange={() => setCompAction('challenge')} /> Send counter-offer
            </label>
          </div>
          {compAction === 'challenge' && (
            <div style={{ display:'flex', gap:'0.65rem', flexWrap:'wrap', marginBottom:'0.65rem' }}>
              <div style={{ flex:'1 1 120px' }}>
                <FieldLabel>Counter hours</FieldLabel>
                <input type="number" value={compCounterHours} onChange={e => setCompCounterHours(e.target.value)} className="ni-input" placeholder={comp.hoursSpent||''} style={{ marginTop:'0.3rem' }} min={0} />
              </div>
              <div style={{ flex:'1 1 120px' }}>
                <FieldLabel>Counter BOM (€)</FieldLabel>
                <input type="number" value={compCounterBom} onChange={e => setCompCounterBom(e.target.value)} className="ni-input" placeholder={comp.bomCost||''} style={{ marginTop:'0.3rem' }} min={0} />
              </div>
            </div>
          )}
          <FieldLabel>Note (optional)</FieldLabel>
          <textarea value={compNote} onChange={e => setCompNote(e.target.value)} className="ni-input" rows={2} placeholder="Explain your position..." style={{ resize:'vertical', marginTop:'0.3rem', marginBottom:'0.65rem' }} />
          <button onClick={onRespond} disabled={submitting} className="ni-button ni-button--primary">
            {submitting ? 'Sending…' : compAction === 'accept' ? 'Accept Compensation Claim' : 'Send Counter-Offer'}
          </button>
        </Card>
      )}

      {/* Confirm payment sent — after acceptance */}
      {(comp.status === 'accepted' || comp.supplierAcceptedAt) && !paymentSent && (
        <Card title="Confirm Payment">
          <p style={{ color:colors.textMuted, fontSize:'0.85rem', margin:'0 0 0.75rem' }}>
            Once you have transferred <strong style={{ color:colors.statusYellow }}>€{(comp.acceptedTotal ?? comp.totalClaimed ?? 0).toLocaleString()}</strong> to North Invent, confirm below.
          </p>
          <FieldLabel>Payment note (optional)</FieldLabel>
          <input value={compPaymentNote} onChange={e => setCompPaymentNote(e.target.value)} className="ni-input" placeholder="Reference number, bank transfer ID..." style={{ marginTop:'0.3rem', marginBottom:'0.65rem' }} />
          <button onClick={onPaymentSent} disabled={submitting} className="ni-button ni-button--primary">
            {submitting ? 'Confirming…' : '✓ Confirm Payment Sent'}
          </button>
        </Card>
      )}
    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────

function Card({ title, children }) {
  return (
    <div style={{ background:colors.surface, border:`1px solid ${colors.panelBorder}`, borderRadius:10, padding:'1rem' }}>
      {title && <div style={{ fontSize:'0.68rem', color:colors.textDim, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'0.65rem' }}>{title}</div>}
      {children}
    </div>
  );
}

function FieldLabel({ children, style }) {
  return <div style={{ fontSize:'0.72rem', color:colors.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', ...style }}>{children}</div>;
}

function Pair({ label, value }) {
  return (
    <div>
      <div style={{ fontSize:'0.65rem', color:colors.textDim, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</div>
      <div style={{ fontSize:'0.9rem', color:colors.textBody, marginTop:'0.15rem' }}>{value??'—'}</div>
    </div>
  );
}

function FileUploadField({ files, setFiles, label, disabled }) {
  if (disabled) return null;
  return (
    // tabIndex makes the field focusable, so a screenshot can be pasted into it
    // without first picking a file (issue #739).
    <div tabIndex={0} onPaste={e => handleImagePaste(e, setFiles)} style={{ outline: 'none' }}>
      <FieldLabel>{label}</FieldLabel>
      <input type="file" multiple accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.csv,.zip"
        onChange={e => setFiles(Array.from(e.target.files))} className="ni-input" style={{ padding:'0.4rem', marginTop:'0.35rem' }} />
      <div style={{ fontSize:'0.72rem', color:colors.textMuted, marginTop:'0.2rem' }}>
        {files.length > 0
          ? `${files.length} file(s) selected \u2014 ${files.map(f => f.name).join(', ')}`
          : 'Or click here and paste a screenshot (Ctrl+V).'}
      </div>
    </div>
  );
}

// Cloudinary force-download variant of a delivery URL (fl_attachment keeps the
// browser from just opening the file inline).
function downloadUrl(url) {
  return url.includes('/upload/') ? url.replace('/upload/', '/upload/fl_attachment/') : url;
}

function AttachedFiles({ files, label = 'Previously Attached' }) {
  if (!files || files.length === 0) return null;
  const isImage = f => !!f.url && (f.mimeType || '').startsWith('image/');
  const images = files.filter(isImage);
  const others = files.filter(f => !isImage(f));
  return (
    <div>
      {label && <FieldLabel>{label}</FieldLabel>}
      {images.length > 0 && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:'0.5rem', marginTop:'0.45rem' }}>
          {images.map(f => (
            <div key={f.id} style={{ width:110 }}>
              <a href={f.url} target="_blank" rel="noreferrer" title={`${f.originalName} — open full size`}>
                <img src={f.url} alt={f.originalName} style={{ width:110, height:82, objectFit:'cover', borderRadius:8, border:`1px solid ${colors.panelBorder}`, display:'block' }} />
              </a>
              <div style={{ display:'flex', alignItems:'center', gap:'0.3rem', marginTop:'0.15rem' }}>
                <span style={{ flex:1, fontSize:'0.65rem', color:colors.textMuted, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{f.originalName}</span>
                <a href={downloadUrl(f.url)} title="Download" style={{ fontSize:'0.72rem', color:colors.primary, textDecoration:'none', flexShrink:0 }}>⬇</a>
              </div>
            </div>
          ))}
        </div>
      )}
      {others.length > 0 && (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.25rem', marginTop:'0.35rem' }}>
          {others.map(f => (
            <div key={f.id} style={{ fontSize:'0.78rem', color:colors.textMuted, display:'flex', alignItems:'center', gap:'0.5rem' }}>
              <span>📎</span>
              {f.url
                ? <a href={f.url} target="_blank" rel="noreferrer" style={{ color:colors.primary }}>{f.originalName}</a>
                : <span>{f.originalName}</span>}
              {f.size ? <span style={{ color:colors.textDim }}>({(f.size/1024).toFixed(0)} KB)</span> : null}
              {f.url
                ? <a href={downloadUrl(f.url)} title="Download" style={{ fontSize:'0.75rem', color:colors.primary, textDecoration:'none' }}>⬇</a>
                : <span style={{ fontSize:'0.68rem', color:colors.textDim, fontStyle:'italic' }}>file no longer available</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
